import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebaseConfig';
import { 
  initializeUserCredits, 
  getUserCredits, 
  consumeCredit as consumeCreditApi,
  createCreditCheckoutSession 
} from '../model/CreditUtils';

interface CreditContextType {
  credits: number;
  loading: boolean;
  error: string | null;
  refreshCredits: () => Promise<void>;
  consumeCredit: (query?: string) => Promise<{ success: boolean; balance: number } | null>;
  purchaseCredits: (amount: number) => Promise<string | null>;
  hasCredits: boolean;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

interface CreditProviderProps {
  children: ReactNode;
}

export const CreditProvider: React.FC<CreditProviderProps> = ({ children }) => {
  const [user] = useAuthState(auth);
  const [credits, setCredits] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refreshCredits = useCallback(async () => {
    if (!user) {
      setCredits(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getUserCredits(user.uid);
      setCredits(result.balance);
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError('Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initialize credits for new users and fetch current balance
  useEffect(() => {
    const initCredits = async () => {
      if (!user) {
        setCredits(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // This will grant initial credits if user is new
        const result = await initializeUserCredits(user.uid);
        setCredits(result.balance);
      } catch (err) {
        console.error('Error initializing credits:', err);
        setError('Failed to initialize credits');
      } finally {
        setLoading(false);
      }
    };

    initCredits();
  }, [user]);

  const consumeCredit = useCallback(async (query?: string) => {
    if (!user) return null;

    try {
      const result = await consumeCreditApi(user.uid, query);
      if (result.success) {
        setCredits(result.balance);
      }
      return result;
    } catch (err: any) {
      console.error('Error consuming credit:', err);
      // Check if it's an insufficient credits error
      if (err.code === 'resource-exhausted' || err.message?.includes('Insufficient credits')) {
        setCredits(0);
        return { success: false, balance: 0 };
      }
      throw err;
    }
  }, [user]);

  const purchaseCredits = useCallback(async (amount: number): Promise<string | null> => {
    if (!user || !user.email) return null;

    try {
      const result = await createCreditCheckoutSession(user.uid, user.email, amount);
      return result.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to create checkout session');
      return null;
    }
  }, [user]);

  const value = {
    credits,
    loading,
    error,
    refreshCredits,
    consumeCredit,
    purchaseCredits,
    hasCredits: credits > 0,
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};

export const useCredits = (): CreditContextType => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};

